import { Suspense, useState, useEffect } from 'react'
import { useLayout } from '@hooks/useLayout'
import { useRouterTransition } from '@hooks/useRouterTransition'
import LayoutWrapper from '@layouts/components/layout-wrapper'
import { Route, Routes, Navigate, useLocation, Outlet, matchPath } from 'react-router-dom'
import { ErrorPageRoute, PreDefinedRoutes } from './routes'
import BlankLayout from '@layouts/BlankLayout'
import VerticalLayout from '../layouts/VerticalLayout'
import HorizontalLayout from '@layouts/HorizontalLayout'
import { useSelector } from 'react-redux'
import ScheduleNotification from '../views/_molecules/ScheduleNotification'
import { SplashScreen } from '../SplashScreen'
import { useFeatureGate } from '../_hooks/useFeatureGate'

const Router = () => {
  const [layout, setLayout] = useLayout()
  const [transition, setTransition] = useRouterTransition()
  const { roles } = useSelector((state) => state.userContextReducer.userContext)
  const userProfile = useSelector((state) => state.profileReducer.profile)
  // AIA-3454 IDH admin can access data marketplace admin
  const isIDHAdmin = userProfile?.isInvestmentDataHubAdmin
  const DefaultLayout = layout === 'horizontal' ? 'HorizontalLayout' : 'VerticalLayout'
  const Layouts = { BlankLayout, VerticalLayout, HorizontalLayout }
  const [allRoutes, setAllRoutes] = useState(PreDefinedRoutes(false) || [])
  const currentActiveItem = null
  const {
    isInInvestmentDesktop,
    userMetaData: { isAppStoreTourCompleted }
  } = useSelector((state) => state?.profileReducer?.profile)
  const location = useLocation()
  const isLandingPage = matchPath('/landing/:page', location.pathname)
  const { value: centrali3_0, isLoading: isCentrali3_0Loading } = useFeatureGate(
    'centrali3_0',
    false
  )

  useEffect(() => {
    setAllRoutes(PreDefinedRoutes(centrali3_0))
  }, [centrali3_0])

  const isRouterLoading = isCentrali3_0Loading

  // Dynamic main route based on centrali3_0 flag
  const dynamicMainRoute = centrali3_0 ? '/my-space' : '/app-store'

  // Check if a route is accessible based on isNewUiAccessible metadata
  const isRouteAccessible = (route) => {
    const isNewUiAccessible = route?.meta?.isNewUiAccessible

    // If undefined, route is accessible in both modes
    if (isNewUiAccessible === undefined) return true

    // If boolean, match with centrali3_0 flag
    return isNewUiAccessible === centrali3_0
  }

  const LayoutRoutesAndPaths = (layout) => {
    const LayoutRoutes = []
    const LayoutPaths = []
    if (allRoutes.length > 0) {
      allRoutes.filter((route) => {
        const isDataProductAdminRoute = route?.path?.startsWith('/data-product-admin')
        if (
          (route.layout === layout || (route.layout === undefined && DefaultLayout === layout)) &&
          (route.meta.isAccessibleBy === undefined ||
            roles?.some((role) => route.meta.isAccessibleBy?.includes(role)) ||
            (isDataProductAdminRoute && isIDHAdmin))
        ) {
          LayoutRoutes.push(route)
          LayoutPaths.push(route.path)
        }
      })
    }

    return { LayoutRoutes, LayoutPaths }
  }

  const ResolveRoutes = () => {
    return Object.keys(Layouts).map((layout, index) => {
      const LayoutTag = Layouts[layout]
      const { LayoutRoutes, LayoutPaths } = LayoutRoutesAndPaths(layout)
      const routerProps = {}

      if (Array.isArray(LayoutPaths) && LayoutPaths.length === 0) {
        return null // Skip rendering if LayoutPaths is empty
      } else {
        return LayoutPaths.map((path, pathIndex) => {
          // Find the route object to check isNewUiAccessible
          const routeObj = LayoutRoutes.find((r) => r.path === path)
          // Redirect to /my-space if user is in new UI mode but trying to access a old route
          const shouldRedirectToMySpace = centrali3_0 && !isRouteAccessible(routeObj)
          // Redirect to error page if user is in old ui but trying to access a new-UI-only route
          const shouldRedirectToError = !centrali3_0 && routeObj?.meta?.isNewUiAccessible === true

          return (
            <Route
              path={path}
              key={`${index}-${pathIndex}`}
              element={
                shouldRedirectToMySpace ? (
                  <Navigate to="/my-space" replace />
                ) : shouldRedirectToError ? (
                  <Navigate to={ErrorPageRoute} replace />
                ) : (
                  <LayoutTag
                    routerProps={routerProps}
                    layout={layout}
                    setLayout={setLayout}
                    transition={transition}
                    setTransition={setTransition}
                    currentActiveItem={currentActiveItem}>
                    <Outlet />
                  </LayoutTag>
                )
              }>
              {LayoutRoutes.map((route) => {
                const isMatchingPath = path === route.path
                if (isMatchingPath) {
                  return (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={
                        <Suspense fallback={null}>
                          <LayoutWrapper
                            layout={route.layout || DefaultLayout}
                            transition={transition}
                            setTransition={setTransition}
                            {...(route.appLayout ? { appLayout: route.appLayout } : {})}
                            {...(route.meta ? { routeMeta: route.meta } : {})}
                            {...(route.className ? { wrapperClass: route.className } : {})}>
                            <route.component meta={route.meta} />
                          </LayoutWrapper>
                        </Suspense>
                      }
                    />
                  )
                }
                return null
              })}
            </Route>
          )
        })
      }
    })
  }

  return (
    <>
      {/* {isCompleteProfileModalDisplayed && <CompleteProfile openModal={setCanCompleteProfile} />} */}
      {!isLandingPage && location.pathname !== '/account-info' && <ScheduleNotification />}
      {isRouterLoading ? (
        <SplashScreen shouldShow={isRouterLoading} />
      ) : (
        <Routes>
          <Route path="/" element={<Navigate to={dynamicMainRoute} />} />
          {ResolveRoutes()}
          <Route path="*" element={<Navigate to={ErrorPageRoute} />} />
        </Routes>
      )}
    </>
  )
}

export default Router
